import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Building2,
  Save,
  AlertCircle,
  CheckCircle,
  FileText,
  CreditCard,
  User,
  MapPin,
  Phone,
  Mail,
  Hash
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Indian states for dropdown
const INDIAN_STATES = [
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Arunachal Pradesh', code: '12' },
  { name: 'Assam', code: '18' },
  { name: 'Bihar', code: '10' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Delhi', code: '07' },
  { name: 'Goa', code: '30' },
  { name: 'Gujarat', code: '24' },
  { name: 'Haryana', code: '06' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Karnataka', code: '29' },
  { name: 'Kerala', code: '32' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Manipur', code: '14' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Mizoram', code: '15' },
  { name: 'Nagaland', code: '13' },
  { name: 'Odisha', code: '21' },
  { name: 'Punjab', code: '03' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Sikkim', code: '11' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Telangana', code: '36' },
  { name: 'Tripura', code: '16' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'West Bengal', code: '19' },
  { name: 'Jammu and Kashmir', code: '01' },
  { name: 'Ladakh', code: '38' },
  { name: 'Chandigarh', code: '04' },
  { name: 'Puducherry', code: '34' },
  { name: 'Andaman and Nicobar Islands', code: '35' },
  { name: 'Dadra and Nagar Haveli', code: '26' },
  { name: 'Lakshadweep', code: '31' },
];

const CompanyMasterSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  
  const [formData, setFormData] = useState({
    legal_name: '',
    gstin: '',
    pan: '',
    registered_address: '',
    state: 'Telangana',
    sac_code: '998314',
    authorized_signatory: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    logo_url: '',
    terms_and_conditions: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCompanyMaster();
  }, []);

  const fetchCompanyMaster = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/invoice/company`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        setFormData({
          legal_name: response.data.legal_name || '',
          gstin: response.data.gstin || '',
          pan: response.data.pan || '',
          registered_address: response.data.registered_address || '',
          state: response.data.state || 'Telangana',
          sac_code: response.data.sac_code || '998314',
          authorized_signatory: response.data.authorized_signatory || '',
          bank_name: response.data.bank_name || '',
          bank_account: response.data.bank_account || '',
          bank_ifsc: response.data.bank_ifsc || '',
          logo_url: response.data.logo_url || '',
          terms_and_conditions: response.data.terms_and_conditions || '',
          email: response.data.email || '',
          phone: response.data.phone || ''
        });
        setHasExisting(true);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // No company master yet, show empty form
        setHasExisting(false);
      } else {
        console.error('Error fetching company master:', err);
        setError('Failed to load company settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateGSTIN = (gstin) => {
    if (!gstin) return true; // Optional
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return pattern.test(gstin.toUpperCase());
  };

  const validatePAN = (pan) => {
    if (!pan) return true; // Optional
    const pattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return pattern.test(pan.toUpperCase());
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal name is required';
    }
    
    if (!formData.gstin.trim()) {
      newErrors.gstin = 'GSTIN is required for invoicing';
    } else if (!validateGSTIN(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format (e.g., 36AABCT1234A1Z5)';
    }
    
    if (!formData.pan.trim()) {
      newErrors.pan = 'PAN is required';
    } else if (!validatePAN(formData.pan)) {
      newErrors.pan = 'Invalid PAN format (e.g., AABCT1234A)';
    }
    
    if (!formData.registered_address.trim()) {
      newErrors.registered_address = 'Registered address is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.authorized_signatory.trim()) {
      newErrors.authorized_signatory = 'Authorized signatory name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_URL}/api/invoice/company`, {
        legal_name: formData.legal_name.trim(),
        gstin: formData.gstin.trim().toUpperCase(),
        pan: formData.pan.trim().toUpperCase(),
        registered_address: formData.registered_address.trim(),
        state: formData.state,
        sac_code: formData.sac_code || '998314',
        authorized_signatory: formData.authorized_signatory.trim(),
        bank_name: formData.bank_name.trim() || null,
        bank_account: formData.bank_account.trim() || null,
        bank_ifsc: formData.bank_ifsc.trim().toUpperCase() || null,
        logo_url: formData.logo_url.trim() || null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSuccess('Company settings saved successfully!');
      setHasExisting(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving company master:', err);
      setError(err.response?.data?.detail || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const getStateCode = (stateName) => {
    const state = INDIAN_STATES.find(s => s.name === stateName);
    return state ? state.code : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Company Master Settings</h1>
                  <p className="text-sm text-gray-500">Configure company details for GST invoicing</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {hasExisting && (
                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Configured
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-8 px-4">
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">GST Invoice Configuration</p>
              <p>These details will appear on all GST invoices generated for wallet recharge transactions. 
                 The GSTIN state code must match your registered state for correct GST calculation.</p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-800">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-gray-600" />
                Basic Information
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="legal_name"
                  value={formData.legal_name}
                  onChange={handleInputChange}
                  placeholder="e.g., TechOrange Legal Solutions Pvt Ltd"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.legal_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.legal_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.legal_name}</p>
                )}
              </div>

              {/* GSTIN and PAN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GSTIN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleInputChange}
                      placeholder="e.g., 36AABCT1234A1Z5"
                      maxLength={15}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                        errors.gstin ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.gstin && (
                    <p className="mt-1 text-sm text-red-600">{errors.gstin}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">15-character GST Identification Number</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="pan"
                      value={formData.pan}
                      onChange={handleInputChange}
                      placeholder="e.g., AABCT1234A"
                      maxLength={10}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                        errors.pan ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.pan && (
                    <p className="mt-1 text-sm text-red-600">{errors.pan}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">10-character Permanent Account Number</p>
                </div>
              </div>

              {/* Registered Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registered Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    name="registered_address"
                    value={formData.registered_address}
                    onChange={handleInputChange}
                    placeholder="Full registered address as per GST registration"
                    rows={3}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.registered_address ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.registered_address && (
                  <p className="mt-1 text-sm text-red-600">{errors.registered_address}</p>
                )}
              </div>

              {/* State and SAC Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.state ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {INDIAN_STATES.map(state => (
                      <option key={state.code} value={state.name}>
                        {state.name} ({state.code})
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    State Code: {getStateCode(formData.state)} (for Place of Supply)
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SAC Code
                  </label>
                  <input
                    type="text"
                    name="sac_code"
                    value={formData.sac_code}
                    onChange={handleInputChange}
                    placeholder="e.g., 998314"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Service Accounting Code (default: 998314 for IT services)</p>
                </div>
              </div>

              {/* Authorized Signatory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authorized Signatory <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="authorized_signatory"
                    value={formData.authorized_signatory}
                    onChange={handleInputChange}
                    placeholder="Name of authorized signatory for invoices"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.authorized_signatory ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.authorized_signatory && (
                  <p className="mt-1 text-sm text-red-600">{errors.authorized_signatory}</p>
                )}
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                Bank Details (Optional)
              </h2>
              <p className="text-sm text-gray-500 mt-1">Bank details will appear on invoices for payment reference</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="e.g., HDFC Bank"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    name="bank_account"
                    value={formData.bank_account}
                    onChange={handleInputChange}
                    placeholder="e.g., 50100123456789"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="bank_ifsc"
                    value={formData.bank_ifsc}
                    onChange={handleInputChange}
                    placeholder="e.g., HDFC0001234"
                    maxLength={11}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-gray-600" />
                Contact Details (Optional)
              </h2>
              <p className="text-sm text-gray-500 mt-1">Contact info will appear on invoices</p>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="billing@yourcompany.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 98765 43210"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-gray-600" />
                Terms & Conditions
              </h2>
              <p className="text-sm text-gray-500 mt-1">These will appear at the bottom of all invoices</p>
            </div>
            <div className="p-6">
              <textarea
                name="terms_and_conditions"
                value={formData.terms_and_conditions}
                onChange={handleInputChange}
                rows={6}
                placeholder="Enter terms and conditions that will appear on invoices...

Example:
1. Payment is due within 30 days of invoice date.
2. Services are non-refundable once credits are used.
3. GST is charged as per applicable rates."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <p className="mt-2 text-xs text-gray-500">Use line breaks for multiple points. This text will be displayed on all generated invoices.</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {hasExisting ? 'Update Settings' : 'Save Settings'}
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CompanyMasterSettings;
